/** Resolve the game server WebSocket URL for dev, prod and headless test. */
export function resolveServerUrl(): string {
  const u = new URL(location.href);
  const override = u.searchParams.get('server');
  if (override) return override;
  const envUrl = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SERVER_URL;
  if (envUrl) return envUrl;
  // Vite dev server runs on :5173; our game server on :8080.
  if (location.port === '5173' || location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return `ws://${location.hostname || '127.0.0.1'}:8080`;
  }
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}`;
}

/** Query-string helpers for headless/automated runs. */
export function autoStart(): { auto: boolean; name: string } {
  const u = new URL(location.href);
  return {
    auto: u.searchParams.get('auto') === '1',
    name: u.searchParams.get('name') ?? `Captain${Math.floor(performance.now()) % 1000}`,
  };
}

/** Weather PREVIEW override, read once from the URL — a render-only dev/owner lever, never gameplay:
 *  `?storm=0.85` pins the RENDERED storm intensity (the sim/wire keep the server's authoritative weather,
 *  so wind/turn/economy are untouched — only the sky, sea tint, rain and lightning follow the override),
 *  and `?eye=1` opens a preview Eye of the Storm off the bow. Both are absent (null/false) in normal play. */
export function weatherOverride(href: string = location.href): { storm: number | null; eye: boolean } {
  try {
    const u = new URL(href);
    const raw = u.searchParams.get('storm');
    const n = raw === null || raw.trim() === '' ? NaN : Number(raw);
    return {
      storm: Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null,
      eye: u.searchParams.get('eye') === '1',
    };
  } catch { return { storm: null, eye: false }; }
}

/**
 * 🌌 AURORA PREVIEW override, read once from the URL — a render-only dev/owner lever, ZERO wire and ZERO
 * sim. The real display is a scheduled EVENT (roughly one night in three and a half, six to eight minutes
 * long, `auroraIntensityAt` in @piratas/sim), which makes auditing its LOOK impossible without waiting up
 * to a couple of hours for one:
 *   `?aurora=1`        pins a full display,
 *   `?aurora=0.5`      pins a half-strength one (the ramp-in / ramp-out look),
 *   `?aurora=breakup`  pins a full display AT its substorm breakup (fast, nervous rays),
 *   `?aurora=0`        pins it OFF (the "before" half of a before/after pair),
 *   absent             → null (the live schedule).
 * The NIGHT gate and the STORM gate still apply on top, so this cannot conjure an aurora at noon or
 * through a gale — "zero effect by day" is a property of the renderer, not of the schedule.
 */
export function auroraOverride(href: string = location.href): { amt: number | null; breakup: number | null } {
  try {
    const raw = (new URL(href).searchParams.get('aurora') ?? '').trim().toLowerCase();
    if (raw === '') return { amt: null, breakup: null };
    if (raw === 'breakup') return { amt: 1, breakup: 1 };
    const n = Number(raw);
    return Number.isFinite(n) ? { amt: Math.max(0, Math.min(1, n)), breakup: 0 } : { amt: null, breakup: null };
  } catch { return { amt: null, breakup: null }; }
}

/** Water-shader override, read once from the URL — a render-only quality lever, ZERO sim and ZERO wire (the
 *  wave GEOMETRY is the shared Gerstner height either way; only the per-pixel lighting differs):
 *    `?water=low`  forces the legacy flat-shaded path (the guaranteed-cheap fallback),
 *    `?water=high` forces the lit path even on a device the auto-probe would have downgraded,
 *    absent        → 'auto' (the renderer probes the GL context + the graphics preset and decides).
 *  Anything unrecognised is 'auto', so a typo can never strand a captain on a broken sea. */
export function waterQuality(href: string = location.href): 'low' | 'high' | 'auto' {
  try {
    const v = (new URL(href).searchParams.get('water') ?? '').trim().toLowerCase();
    return v === 'low' ? 'low' : v === 'high' ? 'high' : 'auto';
  } catch { return 'auto'; }
}

/**
 * Tone-mapping override, read once from the URL — the kill-switch for the filmic grade.
 *
 * The composer's OutputPass applies whatever `renderer.toneMapping` says, ONCE, at the end of the chain,
 * to every pixel of every scene (sea, port, house interior, the Draga ritual, FX, 3D nametags). That is a
 * lot of surface for one enum, so it gets an escape hatch that needs no rebuild and no settings menu:
 *   `?tone=legacy`  → the pre-filmic grade: linear clamp, exposure 1.0 (what the game shipped before ACES),
 *   `?tone=aces`    → force the filmic grade even if the default ever changes,
 *   absent          → 'auto' (the shipped default: ACES Filmic).
 * Anything unrecognised is 'auto', so a typo can never leave a captain staring at a black sea.
 */
export function toneMapping(href: string = location.href): 'legacy' | 'aces' | 'auto' {
  try {
    const v = (new URL(href).searchParams.get('tone') ?? '').trim().toLowerCase();
    return v === 'legacy' ? 'legacy' : v === 'aces' ? 'aces' : 'auto';
  } catch { return 'auto'; }
}

/** White-Whale PREVIEW override, read once from the URL — a render-only dev/owner lever, ZERO wire and ZERO
 *  sim: the real whale is a scheduled server event (first one ~12 min after boot, then 1–2×/day), which makes
 *  auditing its LOOK impossible without waiting. `?whale=1` drives a SYNTHETIC marker (same shape and the same
 *  ~5 Hz cadence the server broadcasts) circling the local ship so the whole surface→sound→breach cycle can be
 *  watched on demand; `?whale=die` runs the same cycle and then fells it, to review the death beat. Absent
 *  ('off') in normal play — when it is on, `net.whale` is ignored for the RENDER only. */
export function whalePreview(href: string = location.href): 'off' | 'cycle' | 'die' {
  try {
    const v = new URL(href).searchParams.get('whale');
    return v === '1' ? 'cycle' : v === 'die' ? 'die' : 'off';
  } catch { return 'off'; }
}

/* ------------------------------ deep-link "sail with me" (no wire) ------------------------------ */

/** Longest crew name we forward to the server (mirrors the server's CREW_PARAM_MAX). */
const CREW_MAX = 24;

/** The crew name a friend's invite link asks us to join — from `?crew=` (or `#crew=`), URL-decoded, trimmed
 *  and length-capped. '' when there is no invite. Read independently of `?auto=`/`?server=`/`?editor=`, so it
 *  never interferes with those. */
export function crewParam(): string {
  try {
    const u = new URL(location.href);
    let v = u.searchParams.get('crew');
    if (!v && u.hash) v = new URLSearchParams(u.hash.replace(/^#/, '')).get('crew');
    const s = (v ?? '').trim();
    return s.length > 0 && s.length <= CREW_MAX ? s : '';
  } catch { return ''; }
}

/** Append a crew intent to a WS URL as a `crew` query param (handles an existing `?` on a `?server=` override).
 *  The server reads it off the upgrade request's URL — server-local routing, not part of the binary protocol. */
export function withCrew(url: string, crew: string): string {
  if (!crew) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}crew=${encodeURIComponent(crew)}`;
}

/** The shareable link that drops a friend into YOUR waters: the game page + `?crew=<yourName>`. */
export function inviteUrl(name: string): string {
  return `${location.origin}${location.pathname}?crew=${encodeURIComponent(name)}`;
}

/** Share a "sail with me" invite via the native share sheet, falling back to the clipboard on desktop. `toast`
 *  reports the clipboard path (and is a no-op-friendly hook for the caller's own feedback channel). */
export async function shareInvite(name: string, toast: (m: string) => void = () => {}): Promise<void> {
  const url = inviteUrl(name || 'Captain');
  const text = "⚓ Sail with me on Sea of Solana — I'll be waiting in these waters. Hoist the black flag!";
  const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string; url?: string }) => Promise<void> };
  if (typeof nav.share === 'function') {
    try { await nav.share({ title: 'Sea of Solana', text, url }); }
    catch { /* the captain cancelled the share sheet — nothing to do (no clipboard fallback here) */ }
    return;
  }
  // No native share (most desktops): copy the link so it can be pasted into any chat.
  try { await navigator.clipboard?.writeText(`${text} ${url}`); toast('🔗 Link copied — send it to your crew!'); }
  catch { toast(`Copy this to invite your crew: ${url}`); }
}
